import {
  type SupplierQualityQuestionKey,
  type SupplierQualityResponseRow,
  QA_SECTION_ORDER,
  QA_SECTION_QUESTIONS,
  QA_TOTAL_QUESTIONS
} from '@tandemone/types'

import { Badge } from '@/components/ui/badge'
import {
  QA_SKIP_CERTS_LABEL,
  SUPPLIER_QUALITY_QUESTION_LABELS,
  SUPPLIER_QUALITY_SECTION_LABELS
} from '@/domains/supplier-onboarding'

import { ProfileEmpty } from './profile-empty'
import { ProfileSection } from './profile-section'

type Props = {
  companyName: string
  responses: SupplierQualityResponseRow[]
  skippedByCert: boolean
}

export function ProfileQuality({
  companyName,
  responses,
  skippedByCert
}: Props) {
  const title = `${companyName} Quality Assessment`
  const byKey = new Map<SupplierQualityQuestionKey, SupplierQualityResponseRow>(
    responses.map(row => [row.questionKey, row])
  )

  if (skippedByCert) {
    return (
      <ProfileSection title={title}>
        <div className='rounded-md border border-border bg-muted/40 p-4 text-sm text-muted-foreground'>
          Skipped because the supplier holds an accredited quality certification
          ({QA_SKIP_CERTS_LABEL}).
        </div>
      </ProfileSection>
    )
  }

  if (responses.length === 0) {
    return (
      <ProfileSection title={title}>
        <ProfileEmpty message='No quality responses submitted.' />
      </ProfileSection>
    )
  }

  const confirmedCount = responses.filter(row => row.answer).length

  return (
    <ProfileSection
      title={title}
      description={`${confirmedCount} of ${QA_TOTAL_QUESTIONS} attestations confirmed`}
    >
      <div className='flex flex-col gap-5'>
        {QA_SECTION_ORDER.map(section => {
          const keys = QA_SECTION_QUESTIONS[section]
          const sectionConfirmed = keys.filter(
            key => byKey.get(key)?.answer
          ).length
          /** Array.sort is stable so the original question order survives
           * within each Yes/No/unanswered group.
           */
          const sortedKeys = [...keys].sort((left, right) => {
            const leftRow = byKey.get(left)
            const rightRow = byKey.get(right)
            const leftRank = leftRow === undefined ? 2 : leftRow.answer ? 0 : 1
            const rightRank =
              rightRow === undefined ? 2 : rightRow.answer ? 0 : 1
            return leftRank - rightRank
          })
          return (
            <div key={section} className='space-y-2'>
              <div className='flex items-baseline justify-between gap-3'>
                <h3 className='text-sm font-semibold text-foreground'>
                  {SUPPLIER_QUALITY_SECTION_LABELS[section]}
                </h3>
                <span className='text-[11px] tabular-nums text-muted-foreground'>
                  {sectionConfirmed}/{keys.length}
                </span>
              </div>
              <dl className='flex flex-col gap-2'>
                {sortedKeys.map(questionKey => {
                  const row = byKey.get(questionKey)
                  return (
                    <div
                      key={questionKey}
                      className='flex flex-col gap-1 rounded-md border border-border bg-card p-3'
                    >
                      <div className='flex items-start justify-between gap-3'>
                        <dt className='text-sm font-medium text-foreground'>
                          {
                            SUPPLIER_QUALITY_QUESTION_LABELS[questionKey]
                              .question
                          }
                        </dt>
                        <dd className='shrink-0'>
                          {row === undefined ? (
                            <Badge variant='outline'>Not answered</Badge>
                          ) : row.answer ? (
                            <Badge className='bg-emerald-100 text-emerald-900 border-transparent dark:bg-emerald-950 dark:text-emerald-200'>
                              Yes
                            </Badge>
                          ) : (
                            <Badge variant='outline'>No</Badge>
                          )}
                        </dd>
                      </div>
                      {row?.answer && row.description ? (
                        <p className='border-l-2 border-border pl-2.5 text-xs leading-relaxed text-ink-soft'>
                          {row.description}
                        </p>
                      ) : null}
                    </div>
                  )
                })}
              </dl>
            </div>
          )
        })}
      </div>
    </ProfileSection>
  )
}
